<template>
  <div class="login-wrapper">
    <div class="login-container">
      <div class="login-header">
        <div class="logo-outer">
          <mail-outlined class="logo-icon" />
        </div>
        <h1 class="login-title">管理后台</h1>
        <p class="login-subtitle">登录系统进行管理</p>
      </div>
      
      <a-card class="login-card" :bordered="false">
        <a-form :model="form" @submit.prevent="handleLogin" layout="vertical" class="login-form">
          <a-form-item label="用户名" required>
            <a-input 
              v-model:value="form.username" 
              size="large" 
              placeholder="请输入用户名"
              class="custom-input"
            >
              <template #prefix>
                <user-outlined class="input-icon" />
              </template>
            </a-input>
          </a-form-item>
          
          <a-form-item label="密码" required>
            <a-input-password 
              v-model:value="form.password" 
              size="large" 
              placeholder="请输入密码"
              class="custom-input"
            >
              <template #prefix>
                <lock-outlined class="input-icon" />
              </template>
            </a-input-password>
          </a-form-item>
          
          <a-form-item class="submit-item">
            <a-button 
              type="primary" 
              html-type="submit" 
              block 
              size="large" 
              :loading="loading"
              class="login-btn"
            >
              登录
            </a-button>
          </a-form-item>
        </a-form>
      </a-card>
      
      <div class="login-footer">
        <p>安全企业级访问</p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons-vue';

export default {
  components: {
    UserOutlined,
    LockOutlined,
    MailOutlined
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const loading = ref(false);
    const form = reactive({
      username: '',
      password: '',
    });

    const handleLogin = async () => {
      if (!form.username || !form.password) {
        return message.warning('请输入用户名和密码');
      }
      
      loading.value = true;
      try {
        await store.dispatch('login', form);
        message.success('登录成功');
        router.push('/');
      } catch (error) {
        message.error('登录失败: ' + (error.response?.data?.error || error.message));
      } finally {
        loading.value = false;
      }
    };

    return { form, handleLogin, loading };
  },
};
</script>

<style scoped>
.login-wrapper {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-navy);
  background-image: 
    radial-gradient(circle at 15% 50%, rgba(5, 150, 105, 0.08), transparent 25%),
    radial-gradient(circle at 85% 30%, rgba(59, 130, 246, 0.05), transparent 25%);
  position: relative;
  overflow: hidden;
}

.login-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
}

.login-container {
  width: 100%;
  max-width: 400px;
  padding: 0 24px;
  position: relative;
  z-index: 1;
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo-outer {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.logo-icon {
  font-size: 28px;
  color: #ffffff;
}

.login-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
}

.login-subtitle {
  color: var(--color-slate-light);
  font-size: 15px;
  margin: 0;
}

.login-card {
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
}

.login-form {
  padding: 8px 0;
}

.login-form :deep(.ant-form-item-label > label) {
  font-weight: 500;
  color: var(--color-navy-light);
}

.custom-input {
  border-radius: 6px;
}

.custom-input :deep(input) {
  font-size: 15px;
}

.input-icon {
  color: var(--color-slate);
}

.login-form :deep(.ant-input-affix-wrapper:focus),
.login-form :deep(.ant-input-affix-wrapper-focused) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.submit-item {
  margin-top: 32px;
  margin-bottom: 0;
}

.login-btn {
  height: 44px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  background-color: #3b82f6;
  border-color: #3b82f6;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.2);
  transition: all 0.2s;
}

.login-btn:hover, .login-btn:focus {
  background-color: #2563eb;
  border-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 6px 8px rgba(59, 130, 246, 0.3);
}

.login-footer {
  text-align: center;
  margin-top: 32px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
</style>
